import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  CompanySettingsResponseSchema,
  getCustomFieldNames,
  type CustomFieldsSchema,
  type CompanySettings,
} from "@forgepoint/shared";
import { captureException } from "@/lib/analytics";
import { fetchApi } from "@/lib/api";

interface CompanySettingsState {
  settings: CompanySettings | null;
  isLoading: boolean;
  isSaving: boolean;
  hasFetched: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  updateSettings: (
    customFieldsSchema: CustomFieldsSchema | null,
  ) => Promise<CompanySettings | undefined>;
}

/** Zustand store for organization-level company settings. */
export const useCompanySettingsStore = create<CompanySettingsState>()(
  immer((set, get) => ({
    settings: null,
    isLoading: false,
    isSaving: false,
    hasFetched: false,
    error: null,

    fetchSettings: async () => {
      if (get().isLoading) {
        return;
      }
      set({ isLoading: true, error: null });
      try {
        const response = await fetchApi("company-settings", "GET");
        if (!response.ok) {
          throw new Error(response.error ?? "Failed to fetch company settings");
        }
        const parsed = CompanySettingsResponseSchema.parse(response.data);
        set({
          settings: parsed.settings,
          isLoading: false,
          hasFetched: true,
        });
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Unknown error");
        captureException(normalizedError);
        set({
          error: normalizedError.message,
          isLoading: false,
          hasFetched: true,
        });
      }
    },

    updateSettings: async (customFieldsSchema) => {
      set({ isSaving: true, error: null });
      try {
        const response = await fetchApi("company-settings", "POST", {
          body: { customFieldsSchema },
        });
        if (!response.ok) {
          throw new Error(
            response.error ?? "Failed to update company settings",
          );
        }
        const parsed = CompanySettingsResponseSchema.parse(response.data);
        if (!parsed.settings) {
          throw new Error("Company settings response was empty");
        }
        set({
          settings: parsed.settings,
          isSaving: false,
          hasFetched: true,
        });
        return parsed.settings;
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Unknown error");
        captureException(normalizedError);
        set({ error: normalizedError.message, isSaving: false });
        return undefined;
      }
    },
  })),
);

/**
 * Reads the org's company custom field names (in configured order), fetching
 * company settings once when they have not been fetched yet.
 */
export function useCompanyCustomFieldNames(): string[] {
  const schema = useCompanySettingsStore(
    (state) => state.settings?.customFieldsSchema ?? null,
  );
  const hasFetched = useCompanySettingsStore((state) => state.hasFetched);
  const fetchSettings = useCompanySettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!hasFetched) {
      fetchSettings();
    }
  }, [hasFetched, fetchSettings]);

  return useMemo(() => getCustomFieldNames(schema), [schema]);
}
